import { z } from 'zod';
import { CampaignSchema } from './campaigns';
import { InstalledCoMarketingTemplateSchema } from './coMarketing';

export const PaginationReqSchema = z.object({
	page: z.number(),
	limit: z.number(),
});

export const CampaignStatusFilterSchema = z.object({
	active: z.boolean(),
	inactive: z.boolean(),
	archived: z.boolean(),
});

export const CampaignReportFiltersSchema = z.object({
	// only stats for actions, conversions, etc that occurred after this date are counted
	startDate: z.number(),
	// if provided, only stats for actions/conversions that occurred <= this date are counted
	endDate: z.number(),
	// if provided, limits results to the given campaign IDs
	campaignIDs: z.array(z.string()),
	// if provided, limits results to campaigns with any the given tags
	campaignTags: z.array(z.string()),
	// if provided, limits results to campaigns with the given status(es)
	campaignStatus: CampaignStatusFilterSchema,
	// if true, use strict attribution rules for converstions, else broad attribution rules
	strictAttribution: z.boolean(),
	// if provided, limits results to the given variant IDs
	variantIDs: z.array(z.string()),
});

export const ListCampaignStatsByCampaignRequestSchema = z.object({
	// only stats for actions, conversions, etc that occurred after this date
	// are counted
	startDate: z.number(),
	// if provided, only stats for actions/conversions that occurred
	// <= this date are counted
	endDate: z.number(),
	// if provided, limits results to the given campaign IDs
	campaignIDs: z.array(z.string()),
	// if provided, limits results to campaigns with any the given tags
	campaignTags: z.array(z.string()),
	// if provided, limits results to campaigns with the given status(es)
	campaignStatus: CampaignStatusFilterSchema,
	// if true, use strict attribution rules for converstions, else broad
	// attribution rules
	strictAttribution: z.boolean(),
	pagination: PaginationReqSchema,
});

export const CampaignStatsByCampaignSchema = z.object({
	campaign: CampaignSchema,
	channels: z.array(z.number()),
	lastMessage: z.number(),
	sendCount: z.number(),
	conversionCount: z.number(),
	cost: z.number(),
	revenue: z.number(),
	conversionPercentage: z.number(),
	roi: z.number(),
	openPercentage: z.number(),
	clickPercentage: z.number(),
});

export const CampaignChannelEngagementSchema = z.object({
	sendCount: z.number(),
	openCount: z.number(),
	clickCount: z.number(),
	cost: z.number(),
	failedSendCount: z.number(),
	unsubscribeCount: z.number(),
	openPercentage: z.number(),
	clickPercentage: z.number(),
	failedSendPercentage: z.number(),
	unsubscribePercentage: z.number(),
	totalPossibleOpens: z.number(),
	totalPossibleClicks: z.number(),
});

export const CampaignChannelPerformanceSchema = CampaignChannelEngagementSchema.extend({
	conversionCount: z.number(),
	conversionRevenue: z.number(),
	// Derived fields
	conversionPercentage: z.number(),
	revenuePerSend: z.number(),
	roi: z.number(),
	roiPercentage: z.number(),
	aovByConverters: z.number(),
});

export const ConversionDataSchema = z.object({
	label: z.string(),
	count: z.number(),
	revenue: z.number(),
	percentage: z.number(),
});

export const CampaignChannelPerformanceResponseSchema = z.object({
	voice: CampaignChannelPerformanceSchema,
	sms: CampaignChannelPerformanceSchema,
	email: CampaignChannelPerformanceSchema,
	push: CampaignChannelPerformanceSchema,
	browser: CampaignChannelPerformanceSchema,
	snail: CampaignChannelPerformanceSchema,
	total: CampaignChannelPerformanceSchema,
	conversionsByAction: z.array(ConversionDataSchema),
	linkClicks: z.record(z.string(), z.number()),
	dynamicLinkClicks: z.record(z.string(), z.number()),
});

export const ConversionDataByCampaignSchema = z.object({
	cmpID: z.string(),
	label: z.string(),
	count: z.number(),
	revenue: z.number(),
});

export const ConversionByRevenueSchema = z.object({
	label: z.string(),
	revenue: z.number(),
});

export const ConversionCountByDateSchema = z.object({
	label: z.string(),
	date: z.string(),
	value: z.number(),
});

export const ConversionsByDateMapSchema = z.record(z.string(), z.array(ConversionCountByDateSchema));

export const ConversionsReportSchema = z.object({
	byRevenue: z.record(z.number()),
	byDate: ConversionsByDateMapSchema,
});

export const CampaignReportCSVCampaignSchema = z.object({
	campaignID: z.string().optional(),
	campaignName: z.string().optional(),
	campaignCreated: z.number().int().optional(),
	lastMessage: z.number().int().optional(),
});

export const CampaignReportCSVEngagementSchema = z.object({
	textSendCount: z.number().int().optional(),
	landingPageOpenCount: z.number().int().optional(),
	uniqueLandingPageOpenCount: z.number().int().optional(),
	textClickCount: z.number().int().optional(),
	mmsCost: z.number().optional(),
	smsCost: z.number().optional(),
	textErrorCount: z.number().int().optional(),
	textUnsubscribeCount: z.number().int().optional(),

	emailSendCount: z.number().int().optional(),
	emailOpenCount: z.number().int().optional(),
	uniqueEmailOpenCount: z.number().int().optional(),
	emailClickCount: z.number().int().optional(),
	emailCost: z.number().optional(),
	emailErrorCount: z.number().int().optional(),
	emailUnsubscribeCount: z.number().int().optional(),

	voiceSendCount: z.number().int().optional(),
	voiceCost: z.number().optional(),
	voiceErrorCount: z.number().int().optional(),
	voiceUnsubscribeCount: z.number().int().optional(),

	pushSendCount: z.number().int().optional(),
	pushOpenCount: z.number().int().optional(),
	uniquePushOpenCount: z.number().int().optional(),
	pushCost: z.number().optional(),
	pushErrorCount: z.number().int().optional(),

	browserSendCount: z.number().int().optional(),
	uniqueBrowserSendCount: z.number().int().optional(),
	browserCost: z.number().optional(),
	browserErrorCount: z.number().int().optional(),
	browserUnsubscribeCount: z.number().int().optional(),

	snailMailSendCount: z.number().int().optional(),
	snailMailCost: z.number().optional(),
	snailMailMailedCount: z.number().int().optional(),
	snailMailReturnedToSenderCount: z.number().int().optional(),
	snailMailInTransitCount: z.number().int().optional(),
	snailMailDeliveredCount: z.number().int().optional(),
	snailMailInLocalAreaCount: z.number().int().optional(),
	snailMailReRoutedCount: z.number().int().optional(),
});

export const CampaignReportCSVConversionsSchema = z.object({
	textConversionCount: z.number().int().optional(),
	textRevenue: z.number().optional(),
	emailConversionCount: z.number().int().optional(),
	emailRevenue: z.number().optional(),
	voiceConversionCount: z.number().int().optional(),
	voiceRevenue: z.number().optional(),
	pushConversionCount: z.number().int().optional(),
	pushRevenue: z.number().optional(),
	browserConversionCount: z.number().int().optional(),
	browserRevenue: z.number().optional(),
	snailMailConversionCount: z.number().int().optional(),
	snailMailRevenue: z.number().optional(),
});

export const CampaignReportCSVLineSchema = CampaignReportCSVCampaignSchema.merge(CampaignReportCSVEngagementSchema)
	.merge(CampaignReportCSVConversionsSchema)
	.extend({
		discountRedemptions: z.number().int().optional(),
		textCost: z.number().optional(),
		textROI: z.number().optional(),
		emailROI: z.number().optional(),
		voiceROI: z.number().optional(),
		pushROI: z.number().optional(),
		browserROI: z.number().optional(),
		snailMailROI: z.number().optional(),
		cost: z.number().optional(),
		revenue: z.number().optional(),
		sendCount: z.number().int().optional(),
		openCount: z.number().int().optional(),
		clickCount: z.number().int().optional(),
		conversionCount: z.number().int().optional(),
		unsubscribeCount: z.number().int().optional(),
		roi: z.number().optional(),
		roiPercentage: z.number().optional(),
		revenuePerSend: z.number().optional(),
		costPerClick: z.number().optional(),
		costPerConversion: z.number().optional(),
		clickPercentage: z.number().optional(),
		conversionPercentage: z.number().optional(),
		converterAOV: z.number().optional(),
	});

export const ConversionReportCSVLineSchema = z.object({
	// campaign fields
	campaignID: z.string().optional(),
	variantID: z.string().optional(),
	// contact fields
	contactID: z.string().optional(),
	// action fields
	label: z.string().optional(),
	channel: z.string().optional(),
	actionTimestamp: z.number().optional(),
	discountID: z.string().optional(),
	// sale/order fields
	isOnline: z.boolean().optional(),
	grossRevenue: z.number().optional(),
	netRevenue: z.number().optional(),
	platform: z.string().optional(),
	saleTimestamp: z.number().optional(),
});

export const CoMarketingRetailerCampaignListItemSchema = z.object({
	campaign: CampaignSchema,
	template: InstalledCoMarketingTemplateSchema.nullable(),
	retailerName: z.string(),
	revenue: z.number(),
	conversionCount: z.number(),
	channels: z.array(z.number()),
});

export const TopConversionsByProductSchema = z.object({
	name: z.string(),
	quantity: z.number(),
	revenue: z.number(),
	category: z.string(),
	skus: z.array(z.string()),
});

export const TopConversionsByBrandSchema = z.object({
	name: z.string(),
	quantity: z.number(),
	revenue: z.number(),
});

export const TopConversionsByLocationSchema = z.object({
	storeID: z.number(),
	quantity: z.number(),
	revenue: z.number(),
	name: z.string(),
	street: z.string(),
	city: z.string(),
	state: z.string(),
	country: z.string(),
	zip: z.string(),
});

export const ConversionLineSchema = z.object({
	id: z.string(),
	campaignID: z.string(),
	variantID: z.string().nullable(),
	contactID: z.string(),
	label: z.string(),
	discountID: z.string().nullable(),
	actionTimestamp: z.number(),
	platform: z.string(),
	paymentMethod: z.string(),
	price: z.number(),
	cost: z.number(),
	saleTimestamp: z.number(),
});
